import {
    IntegrationConfigField,
    IntegrationModal,
    IntegrationsSubSection,
    IntegrationSubSectionRecord,
    IntegrationTabStructure,
} from '../../interface/integrationmodal.interface';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';

class RecordData {
    fields?: IntegrationModal['fields'];

    subSections?: IntegrationModal['subSections'];

    constructor(record: AppIntegrationRecord, metaData: IntegrationModal, fileFieldIds: string[]) {
        const checkAndAssignValue = (
            objectToAssign: any,
            property: string,
            value: any,
            decodingType: IntegrationConfigField['decodingType'],
            isArray?: boolean,
            valueType?: IntegrationConfigField['type']
        ) => {
            if (value !== undefined) {
                if (isArray) {
                    value = Array.isArray(value) ? value : [value];
                } else {
                    value = Array.isArray(value) ? value[0] : value;
                }
            }
            if (valueType === 'FILE') {
                if (Array.isArray(value) && typeof value?.[0] === 'string') {
                    value = value.reduce((acc, val) => {
                        acc[val.substring(val.lastIndexOf('/') + 1)] = convertStringToFile(val, val.substring(val.lastIndexOf('/') + 1));
                        return acc;
                    }, {});
                } else if (typeof value === 'object' && typeof value[Object.keys(value)[0]] === 'string') {
                    value = Object.entries(value).reduce((acc, [fileName, fileString]) => {
                        acc[fileName.substring(fileName.lastIndexOf('/') + 1)] = convertStringToFile(
                            fileString as string,
                            fileName.substring(fileName.lastIndexOf('/') + 1)
                        );
                        return acc;
                    }, {});
                } else if (typeof value === 'string') {
                    value = {
                        [value.substring(value.lastIndexOf('/') + 1)]: convertStringToFile(
                            value,
                            value.substring(value.lastIndexOf('/') + 1)
                        ),
                    };
                }
            }
            if (decodingType) {
                switch (decodingType) {
                    case 'atob':
                        try {
                            objectToAssign[property] = window.atob(value);
                        } catch {
                            objectToAssign[property] = value;
                        }
                        break;
                    case 'DATE':
                        if (CommonUtilsService.isIsoFormat(value)) {
                            objectToAssign[property] = CommonUtilsService.transformDateToLocale(
                                value,
                                'YYYY-MM-DDTHH:MM:SSZ',
                                'DD-MM-YYYY HH:MM:SS AM',
                                false
                            );
                        }
                        break;
                    case 'STRING':
                        try {
                            objectToAssign[property] = JSON.stringify(value);
                        } catch {
                            objectToAssign[property] = value;
                        }
                        break;
                    default:
                        objectToAssign[property] = value;
                        break;
                }
            } else {
                objectToAssign[property] = value;
            }
        };

        const convertStringToFile = (base64Data: string, fileName: string) => {
            let mime = 'application/octet-stream';

            // Trim whitespace/newlines
            base64Data = base64Data.trim();

            // Extract extension (if any)
            const extension = fileName?.split('.').pop()?.toLowerCase();

            // MIME types by extension
            const mimeByExt: Record<string, string> = {
                pdf: 'application/pdf',
                png: 'image/png',
                jpg: 'image/jpeg',
                jpeg: 'image/jpeg',
                gif: 'image/gif',
                tiff: 'image/tiff',
                ico: 'image/x-icon',
                zip: 'application/zip',
                xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                csv: 'text/csv',
                txt: 'text/plain',
            };

            // Handle data URLs
            const matches = base64Data.match(/^data:(.*?);base64,(.*)$/);
            if (matches) {
                mime = matches[1];
                base64Data = matches[2];
            } else if (extension && mimeByExt[extension]) {
                // Use MIME from file extension
                mime = mimeByExt[extension];
            } else {
                // Fallback to content-based detection
                if (base64Data.startsWith('UEsDB')) mime = 'application/zip'; // ZIP or XLSX
                else if (base64Data.startsWith('JVBERi0')) mime = 'application/pdf';
                else if (base64Data.startsWith('/9j/')) mime = 'image/jpeg';
                else if (base64Data.startsWith('iVBORw0KGgo')) mime = 'image/png';
                else if (base64Data.startsWith('R0lGOD')) mime = 'image/gif';
                else if (base64Data.startsWith('SUkq')) mime = 'image/tiff';
                else if (base64Data.startsWith('AAABAAEAEBA')) mime = 'image/x-icon';
                else if (base64Data.startsWith('PK')) mime = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'; // XLSX
                else mime = 'application/zip'; // Default fallback
            }

            // Decode base64 → binary data
            const byteCharacters = atob(base64Data);
            const byteArrays: Uint8Array[] = [];

            for (let offset = 0; offset < byteCharacters.length; offset += 512) {
                const slice = byteCharacters.slice(offset, offset + 512);
                const byteNumbers = new Array(slice.length);

                for (let i = 0; i < slice.length; i++) {
                    byteNumbers[i] = slice.charCodeAt(i);
                }

                byteArrays.push(new Uint8Array(byteNumbers));
            }

            const blob = new Blob(byteArrays as BlobPart[], { type: mime });
            const file = fileName ? new File([blob], fileName, { type: mime }) : undefined;
            return file;
        };

        const setPanaelValue = field => {
            const keys = Object.keys(field.value);
            keys.forEach(key => {
                field.options.forEach(each => {
                    if (each.name === key) {
                        each.value = field.value[key];
                    }
                });
            });
        };

        const checkTabsForObject = (key: string, data: object, tabs: IntegrationTabStructure[]) => {
            let foundAndAssigned = false;
            tabs?.forEach(tab => {
                if (foundAndAssigned) return;
                if (tab.id === key && tab.isObject && typeof data[key] === 'object') {
                    if (!tab.keyFieldId) {
                        return;
                    }
                    foundAndAssigned = true;
                    tab.records = [];
                    Object.keys(data[key] || {}).forEach(subKey => {
                        const newRecord: IntegrationTabStructure['records'][0] = {
                            fields: [],
                            recordActions: [],
                        };
                        tab.fields.forEach(field => {
                            newRecord.fields.push(CommonUtilsService.cloneObject(field));
                        });
                        tab.recordActions?.forEach(action => {
                            newRecord.recordActions.push(CommonUtilsService.cloneObject(action));
                        });
                        const keyField = newRecord.fields.find(tabField => tabField.id === tab.keyFieldId);
                        keyField && checkAndAssignValue(keyField, 'value', subKey, keyField.decodingType, keyField.array, keyField.type);
                        if (tab.valueFieldId) {
                            const valueField = newRecord.fields.find(tabField => tabField.id === tab.valueFieldId);
                            valueField &&
                                checkAndAssignValue(
                                    valueField,
                                    'value',
                                    data[key][subKey],
                                    valueField.decodingType,
                                    valueField.array,
                                    valueField.type
                                );
                        } else {
                            Object.keys(data[key][subKey]).forEach(subSubKey => {
                                const valueField = newRecord.fields.find(tabField => tabField.id === subSubKey);
                                if (valueField) {
                                    checkAndAssignValue(
                                        valueField,
                                        'value',
                                        data[key][subKey][subSubKey],
                                        valueField.decodingType,
                                        valueField.array,
                                        valueField.type
                                    );
                                }
                            });
                        }
                        tab.records.push(newRecord);
                    });
                } else if ((!tab.id || tab.noSubObjectForFields) && !tab.array) {
                    tab.fields.forEach(field => {
                        if (field.id === key) {
                            foundAndAssigned = true;
                            checkAndAssignValue(field, 'value', data[key], field.decodingType, field.array, field.type);
                        }
                    });
                }
                if (!foundAndAssigned && tab.childSubSections?.length > 0) {
                    foundAndAssigned = checkSubsectionsForObject(key, data, tab.childSubSections);
                }
                if (foundAndAssigned === true) {
                    return;
                }
            });
            return foundAndAssigned;
        };

        const checkSubsectionForObject = (key: string, data: object, subRecordObject: IntegrationSubSectionRecord) => {
            let foundAndAssigned = false;
            subRecordObject?.childSubSections?.forEach(subSection => {
                if (foundAndAssigned) return;
                if (subSection.tabs?.length > 0) {
                    foundAndAssigned = checkTabsForObject(key, data, subSection.tabs);
                }
                if (foundAndAssigned) return;
                foundAndAssigned = subSection?.childSubSections?.length > 0 && checkSubsectionForObject(key, data, subSection);
            });
            return foundAndAssigned;
        };

        const loopThroughObject = (key: string, data: object, isFieldRequired?: boolean, subRecordObject?: IntegrationSubSectionRecord) => {
            let field: IntegrationConfigField;
            if (!isFieldRequired) {
                let fieldsArray;
                if (subRecordObject) {
                    fieldsArray = subRecordObject.fields;
                } else {
                    fieldsArray = this.fields;
                }
                field = fieldsArray.find(metaDataField => metaDataField.id === key || metaDataField.fieldIdInSubObject === key);
            }
            if (field) {
                checkAndAssignValue(
                    field,
                    'value',
                    data[key] !== undefined ? data[key] : field.defaultValue,
                    field.decodingType,
                    field.array,
                    field.type
                );
                if ((field.array && Array.isArray(field.value)) || field.valueAsArrayInPayload) {
                    let values = [];
                    if (Array.isArray(field.value)) {
                        values = ((field.value as string[]) && field.value[0] && field.value[0].length > 0 && field.value) || [''];
                    } else {
                        values = [field.value];
                    }
                    field.values = [];
                    if (!Array.isArray(values)) {
                        values = field.value.split(',');
                    }
                    values &&
                        values.forEach(value => {
                            const valueObj: IntegrationConfigField['values'][0] = {};
                            checkAndAssignValue(valueObj, 'value', value, field.decodingType, field.array, field.type);
                            field.values.push(valueObj);
                        });
                }
                if (typeof field.value === 'object' && field.type === 'TRANSFORMATION_PANEL') {
                    setPanaelValue(field);
                }
            } else if (
                !field &&
                subRecordObject &&
                typeof data[key] === 'object' &&
                !Array.isArray(data[key]) &&
                !fileFieldIds.includes(key)
            ) {
                // tslint:disable-next-line:forin
                const found = checkSubsectionForObject(key, data, subRecordObject);
                if (!found) {
                    let objectToLoop = data[key];
                    if (typeof objectToLoop === 'string') {
                        objectToLoop = JSON.parse(objectToLoop);
                    }
                    for (const subKey in objectToLoop) {
                        let noField = false;
                        if (key === 'authentication') {
                            noField = true;
                        }
                        loopThroughObject(subKey, objectToLoop, noField, subRecordObject);
                    }
                }
            } else if (!field && (subRecordObject?.childSubSections?.length > 0 || this.subSections)) {
                let subSections: IntegrationsSubSection[];
                if (subRecordObject?.childSubSections?.length > 0) {
                    subSections = subRecordObject.childSubSections;
                } else {
                    subSections = this.subSections;
                }
                checkSubsectionsForObject(key, data, subSections);
            }
        };

        const isInOnceTab = (cron: string) => cron?.split(' ')?.length == 7 && !['*', '?', '0'].includes(cron?.split(' ')[6]);

        const checkSubsectionsForObject = (key: string, data: object, subSections: IntegrationsSubSection[]) => {
            let foundAndAssigned = false;
            subSections.forEach(subSectionObject => {
                if (foundAndAssigned) return;
                let subField: IntegrationConfigField;
                if (subSectionObject.haveTabs) {
                    for (let i = 0; i < subSectionObject.tabs.length; i++) {
                        const tab = subSectionObject.tabs[i];
                        if (data[key] instanceof Array) {
                            if (tab.id === key) {
                                foundAndAssigned = true;
                                if (data[key].length > 0) {
                                    tab.records = [];
                                    data[key].forEach(subData => {
                                        const newRecord: IntegrationTabStructure['records'][0] = {
                                            fields: [],
                                            recordActions: [],
                                        };
                                        for (const dataKey in subData) {
                                            let metaDataField = tab.fields.find(tabField => tabField.id === dataKey);
                                            if (metaDataField) {
                                                metaDataField = CommonUtilsService.cloneObject(metaDataField);
                                                if (
                                                    metaDataField.valueAsArrayInPayload &&
                                                    Array.isArray(subData[dataKey]) &&
                                                    !metaDataField.array
                                                ) {
                                                    subData[dataKey] = subData[dataKey][0];
                                                } else if (subData[dataKey] && Array.isArray(subData[dataKey])) {
                                                    for (let index = 0; index < subData[dataKey].length; index++) {
                                                        if (subData[dataKey][index] === null || subData[dataKey][index] === undefined) {
                                                            subData[dataKey][index] =
                                                                metaDataField.defaultValue !== undefined ? metaDataField.defaultValue : '';
                                                        }
                                                    }
                                                }
                                                checkAndAssignValue(
                                                    metaDataField,
                                                    'value',
                                                    subData[dataKey] !== undefined ? subData[dataKey] : metaDataField.defaultValue,
                                                    metaDataField.decodingType,
                                                    metaDataField.array,
                                                    metaDataField.type
                                                );
                                                newRecord['fields'].push(metaDataField);
                                            } else {
                                                let metaDataSubField = tab?.recordActions?.find(tabSubField => tabSubField.id === dataKey);
                                                if (metaDataSubField) {
                                                    metaDataSubField = CommonUtilsService.cloneObject(metaDataSubField);
                                                    checkAndAssignValue(
                                                        metaDataSubField,
                                                        'value',
                                                        subData[dataKey] !== undefined ? subData[dataKey] : metaDataSubField.defaultValue,
                                                        metaDataSubField.decodingType,
                                                        metaDataSubField.array,
                                                        metaDataSubField.type
                                                    );
                                                    newRecord['recordActions'].push(metaDataSubField);
                                                }
                                            }
                                        }
                                        if (newRecord.fields?.length < tab.fields?.length) {
                                            (tab as IntegrationTabStructure).fields
                                                .filter(tabField => !newRecord.fields?.find(recordField => recordField.id === tabField.id))
                                                .forEach(tabField => {
                                                    const cloneField = CommonUtilsService.cloneObject(tabField);
                                                    checkAndAssignValue(
                                                        cloneField,
                                                        'value',
                                                        tabField.defaultValue,
                                                        tabField.decodingType,
                                                        tabField.array,
                                                        tabField.type
                                                    );
                                                    newRecord.fields.push(cloneField);
                                                });
                                        }
                                        if (newRecord.recordActions?.length < (tab as IntegrationTabStructure).recordActions?.length) {
                                            (tab as IntegrationTabStructure).recordActions
                                                .filter(
                                                    tabActionField =>
                                                        !newRecord.recordActions?.find(
                                                            recordActionField => recordActionField.id === tabActionField.id
                                                        )
                                                )
                                                .forEach(tabActionField => {
                                                    const cloneActionField = CommonUtilsService.cloneObject(tabActionField);
                                                    checkAndAssignValue(
                                                        cloneActionField,
                                                        'value',
                                                        tabActionField.defaultValue,
                                                        tabActionField.decodingType,
                                                        tabActionField.array,
                                                        tabActionField.type
                                                    );
                                                    newRecord.recordActions.push(cloneActionField);
                                                });
                                        }
                                        newRecord.fields.sort((a, b) => {
                                            const aIndex = tab.fields.findIndex(field => field.id === a.id);
                                            const bIndex = tab.fields.findIndex(field => field.id === b.id);
                                            return aIndex - bIndex;
                                        });
                                        tab.records.push(newRecord);
                                    });
                                } else {
                                    tab.records = tab.records || [];
                                }
                            }
                        } else {
                            if (tab.id === key) {
                                foundAndAssigned = true;
                                if (typeof data[key] === 'string' && tab.valueFieldId) {
                                    subField = tab.fields.find(metaDataField => metaDataField.id === tab.valueFieldId);
                                }
                            } else {
                                subField = tab.fields.find(metaDataField => metaDataField.id === key);
                                if (!subField && data?.['grantType']) {
                                    for (let i = 0; i < tab.fields.length; i++) {
                                        if (!tab.fields[i].conditionalDisplay) {
                                            if (tab.fields[i].id === key) {
                                                subField = tab.fields[i];
                                                break;
                                            }
                                        } else {
                                            if (tab.fields[i].id === key && key === 'grantType') {
                                                subField = tab.fields[i];
                                                break;
                                            }
                                            if (tab.fields[i].id === key && checkForGrantType(tab.fields[i], data['grantType'])) {
                                                subField = tab.fields[i];
                                                break;
                                            }
                                            // return ((metaDataField.id === key) && checkForGrantType(metaDataField, data['grantType']))
                                        }
                                    }
                                }
                                if (!subField && tab.childSubSections?.length > 0) {
                                    foundAndAssigned = checkSubsectionsForObject(key, data, tab.childSubSections);
                                }
                            }
                            // subField = tab.fields.find(metaDataField => metaDataField.id === key);
                            if (subField) {
                                foundAndAssigned = true;
                                break;
                            }
                        }
                    }
                } else if (subSectionObject.structure === 'RECORDS_FORM' && subSectionObject.array && Array.isArray(data[key])) {
                    subSectionObject.records = [];
                    data[key].forEach((configData: AppIntegrationRecord['configs'][0]) => {
                        createSubSectionRecord(subSectionObject.records, subSectionObject, configData);
                    });
                } else if (
                    subSectionObject?.id === key &&
                    subSectionObject.array &&
                    !Array.isArray(data[key]) &&
                    typeof data[key] === 'object' &&
                    data[key] !== null
                ) {
                    foundAndAssigned = true;
                    subSectionObject.records = [];
                    Object.entries(data[key]).forEach(([subKey, value]: [subKey: string, value: any]) => {
                        let recordObject: any = {};
                        recordObject[subSectionObject?.keyFieldId] = subKey;
                        if (subSectionObject?.valueFieldId) {
                            recordObject[subSectionObject?.valueFieldId] = value;
                        } else {
                            recordObject = {
                                ...recordObject,
                                ...value,
                            };
                        }
                        createSubSectionRecord(subSectionObject.records, subSectionObject, recordObject);
                    });
                } else {
                    if (
                        subSectionObject.structure === 'FORM' ||
                        subSectionObject.structure === 'HTTP' ||
                        subSectionObject.structure === 'RECORD_FORM'
                    ) {
                        const formSubField = subSectionObject.fields.find(formSubFieldObj => formSubFieldObj.id === key);
                        foundAndAssigned = !!formSubField;
                        if (formSubField?.type === 'ORGANIZATIONS_CHECKBOX') {
                            const values = [];
                            data[formSubField.id]?.forEach(valueObject => {
                                values.push(valueObject[formSubField.fieldIdInPayloadAs]);
                            });
                            checkAndAssignValue(
                                formSubField,
                                'value',
                                [values],
                                formSubField.decodingType,
                                formSubField.array,
                                formSubField.type
                            );
                        } else if (formSubField) {
                            checkAndAssignValue(
                                formSubField,
                                'value',
                                data[formSubField.id],
                                formSubField.decodingType,
                                formSubField.array,
                                formSubField.type
                            );
                        }
                    } else if (subSectionObject.structure === 'FLEX_FORM') {
                        if (
                            (subSectionObject.id === 'schedulerExpression' || subSectionObject.id === 'cronExpression') &&
                            (key === 'schedulerExpression' || key === 'cronExpression')
                        ) {
                            const formSubField = subSectionObject.fields.find(
                                formSubFieldObj => formSubFieldObj.id === 'schedulerExpression'
                            );
                            const value = data[formSubField.id];
                            let valueToPass;
                            if (isInOnceTab(value)) {
                                valueToPass = value;
                            } else {
                                valueToPass = value
                                    ?.split(' ')
                                    ?.filter((_item, index) => index > 0 && index < 6)
                                    .join(' ');
                            }
                            formSubField &&
                                checkAndAssignValue(
                                    formSubField,
                                    'value',
                                    valueToPass,
                                    formSubField.decodingType,
                                    formSubField.array,
                                    formSubField.type
                                );
                        }
                    }
                }
                if (subField) {
                    foundAndAssigned = true;
                    checkAndAssignValue(
                        subField,
                        'value',
                        data[key] !== undefined ? data[key] : subField && subField.defaultValue,
                        (subField && subField.decodingType) || undefined,
                        (subField && subField.array) || undefined,
                        (subField && subField.type) || undefined
                    );
                }
                if (!subField && subSectionObject?.childSubSections?.length > 0) {
                    checkSubsectionsForObject(key, data, subSectionObject.childSubSections);
                }
            });
            return foundAndAssigned;
        };

        const checkForGrantType = (field, type) => {
            if (field && field.conditionalDisplay) {
                for (let i = 0; i < field.conditionalDisplay.length; i++) {
                    if (field.conditionalDisplay[i].conditions && field.conditionalDisplay[i].conditions.length > 0) {
                        for (let j = 0; j < field.conditionalDisplay[i].conditions.length; j++) {
                            if (
                                field.conditionalDisplay[i].conditions[j].fieldId === 'grantType' &&
                                field.conditionalDisplay[i].conditions[j].requiredValue === type
                            ) {
                                return true;
                            }
                        }
                    }
                }
            }
            return false;
        };

        const assignFieldValue = (field: IntegrationConfigField) => {
            checkAndAssignValue(
                field,
                'value',
                field.value !== undefined ? field.value : field.defaultValue,
                field.decodingType,
                field.array,
                field.type
            );
            if (field.array) {
                let values = field.value !== undefined && (field.value as string[]).length > 0 ? (field.value as string[]) : [''];
                field.values = [];
                if (!Array.isArray(values)) {
                    values = field.value.split(',');
                }
                if (Array.isArray(values)) {
                    values &&
                        values.forEach(value => {
                            field.values.push({ value });
                        });
                }
            }
        };

        const assignSubSectionTabRecord = (tab: IntegrationTabStructure) => {
            if (tab.array) {
                tab.records = tab.records || [];
                if (tab.fieldsType === 'UNIQUE-SBS') {
                    tab.fields.forEach(field => {
                        const index = tab.records.findIndex(tabRecord => tabRecord.fields[0].id === field.id);
                        if (index === -1) {
                            tab.records.push({
                                fields: [CommonUtilsService.cloneObject(field)],
                                recordActions: [],
                            });
                        }
                    });
                } else if (tab.records.length === 0) {
                    tab.records.push({
                        fields: [],
                        recordActions: [],
                    });
                }
                tab.records.forEach(tabRecord => {
                    if (!tabRecord.fields || tabRecord.fields.length === 0) {
                        tab.fields.forEach(tabField => {
                            const newTabField: IntegrationConfigField = CommonUtilsService.cloneObject(tabField);
                            checkAndAssignValue(
                                newTabField,
                                'value',
                                newTabField.defaultValue,
                                newTabField.decodingType,
                                newTabField.array,
                                newTabField.type
                            );
                            tabRecord.fields.push(newTabField);
                        });
                    } else if (tabRecord.fields.length !== tab.fields.length && tab.fieldsType !== 'UNIQUE-SBS') {
                        tab.fields
                            .filter(action => tabRecord.fields.findIndex(recordAction => recordAction.id === action.id) === -1)
                            .forEach(action => {
                                const cloneField = CommonUtilsService.cloneObject(action);
                                checkAndAssignValue(
                                    cloneField,
                                    'value',
                                    action.defaultValue,
                                    action.decodingType,
                                    action.array,
                                    action.type
                                );
                                tabRecord.fields.push(cloneField);
                            });
                    }
                    if (!tabRecord.recordActions || tabRecord.recordActions.length === 0) {
                        tab?.recordActions?.forEach(tabField => {
                            const newTabField: IntegrationConfigField = CommonUtilsService.cloneObject(tabField);
                            checkAndAssignValue(
                                newTabField,
                                'value',
                                newTabField.defaultValue,
                                newTabField.decodingType,
                                newTabField.array,
                                newTabField.type
                            );
                            tabRecord.recordActions.push(newTabField);
                        });
                    } else if (tabRecord.recordActions.length !== tab.recordActions.length) {
                        tab.recordActions
                            .filter(action => tabRecord.recordActions.findIndex(recordAction => recordAction.id === action.id) === -1)
                            .forEach(action => {
                                const cloneAction = CommonUtilsService.cloneObject(action);
                                checkAndAssignValue(
                                    cloneAction,
                                    'value',
                                    action.defaultValue,
                                    action.decodingType,
                                    action.array,
                                    action.type
                                );
                                tabRecord.recordActions.push(cloneAction);
                            });
                    }
                });
            }
        };

        const createSubSectionRecord = (
            records,
            subSectionObject: IntegrationsSubSection,
            recordData?: AppIntegrationRecord['configs'][0]
        ) => {
            const recordObject: IntegrationsSubSection['records'][0] = {
                fields: CommonUtilsService.cloneObject(subSectionObject.fields),
            };
            if (subSectionObject.hasChildSubSectons) {
                recordObject.childSubSections = [];
                recordObject.childSubSections = CommonUtilsService.cloneObject(subSectionObject.childSubSections);
                // tslint:disable-next-line: no-use-before-declare
                assignSubSections(recordObject.childSubSections);
            }
            const recordKeys = (recordData && Object.keys(recordData)) || [];
            if (recordData && recordKeys.length > 0) {
                if (typeof recordData.configuredData === 'string') {
                    // recordData.configuredData = JSON.parse(recordData.configuredData);
                    const parsedData = JSON.parse(recordData.configuredData);
                    Object.keys(parsedData).forEach(key => {
                        loopThroughObject(key, parsedData, undefined, recordObject);
                    });
                }
                recordKeys.forEach(key => {
                    loopThroughObject(key, recordData, undefined, recordObject);
                });
            }
            records.push(recordObject);
        };

        const assignSubSections = (subSections: IntegrationsSubSection[]) => {
            subSections &&
                subSections.forEach(subSectionObject => {
                    if (subSectionObject.haveTabs && subSectionObject.tabs.length > 0) {
                        subSectionObject.tabs.forEach(assignSubSectionTabRecord);
                    } else if (subSectionObject.array) {
                        if (!subSectionObject.records || subSectionObject.records.length === 0) {
                            subSectionObject.records = [];
                            createSubSectionRecord(subSectionObject.records, subSectionObject);
                        }
                    }
                    if (subSectionObject.hasChildSubSectons) {
                        assignSubSections(subSectionObject.childSubSections);
                    }
                });
        };

        for (const key in metaData) {
            if (metaData[key] instanceof Array) {
                // this[key] = metaData[key].slice(0);
                this[key] = CommonUtilsService.cloneObject(metaData[key]);
            } else if (metaData[key] instanceof Object) {
                this[key] = CommonUtilsService.cloneObject(metaData[key]);
            } else {
                this[key] = metaData[key];
            }
        }

        const originalRecordDataKeys = (record && Object.keys(record)) || [];
        if (originalRecordDataKeys?.length > 0) {
            originalRecordDataKeys.forEach(key => {
                loopThroughObject(key, record);
            });
        }
        this.fields?.forEach(assignFieldValue);
        assignSubSections(this.subSections);
    }
}

// tslint:disable-next-line: max-classes-per-file
export class AppIntegrationRecord {
    chainName: string;

    configName: string;

    configs: {
        chainName: string;
        configName: string;
        configuredData: string;
        createdBy: string;
        createdOn: string;
        id: string;
        integrationMethod: string;
        integrationType: string;
        locationId: string;
        resourceName: string;
        sourceName: string;
        schedulerExpression: string;
        serviceId: string;
        transformationName: string;
        chainConnectors: any[];
        isFanoutRequest?: boolean;
    }[];

    integrationType: string;

    locationId: string;

    schedulerExpression: string;

    serviceId: string;

    transformationName: string;

    appId?: string;

    tableValues?: {
        [property: string]: {
            displayName: string;
            id: string;
            value: any;
        };
    };

    recordData?: IntegrationModal;

    originalData?: {
        chainName: string;
        configName: string;
        configs: any[];
        integrationType: string;
        locationId: string;
        schedulerExpression: string;
        serviceId: string;
        resourceName?: string;
        transformationName: string;
        id?: string;
        createdOn?: string;
    };

    isValid?: boolean;

    constructor(record: AppIntegrationRecord, metaData: IntegrationModal) {
        if (metaData?.fieldsToRemoveFromResponse?.length > 0) {
            metaData?.fieldsToRemoveFromResponse.forEach(field => {
                if (field.includes('.')) {
                    const child = field.split('.').slice(-1)[0];
                    delete this.getParentObject(record, field.split('.').slice(0, -1))?.[child];
                } else {
                    delete record[field];
                }
            });
        }
        this.chainName = record?.chainName;
        this.configName = record?.configName;
        this.transformationName = record?.transformationName;
        this.appId = record?.appId;
        this.originalData =
            (record?.originalData && CommonUtilsService.cloneObject(record.originalData)) || CommonUtilsService.cloneObject(record);
        this.isValid = record?.isValid !== undefined ? record.isValid : false;
        const originalData = CommonUtilsService.cloneObject(this.originalData as any);
        const fileFieldIds = this.getFileFieldIds(metaData?.fields, metaData?.subSections, undefined);
        this.recordData = new RecordData(originalData, metaData, fileFieldIds);
        this.tableValues = {};
        this.recordData.tableFieldIds
            ?.filter(fieldId => ['Actions'].indexOf(fieldId) === -1)
            ?.forEach(fieldId => {
                const field = this.recordData.fields.find(dataField => dataField.id === fieldId);
                if (field) {
                    this.tableValues[fieldId] = {
                        displayName: field.displayName,
                        id: fieldId,
                        value: field.value,
                    };
                } else if (!field && this.recordData.subSections) {
                    this.recordData.subSections.forEach(subFieldObject => {
                        let subField: IntegrationConfigField;
                        if (subFieldObject.haveTabs) {
                            for (let i = 0; i < subFieldObject.tabs.length; i++) {
                                const tab = subFieldObject.tabs[i];
                                subField = tab.fields.find(metaDataField => metaDataField.id === fieldId);
                                if (subField) {
                                    break;
                                }
                            }
                        }
                        if (subField) {
                            this.tableValues[fieldId] = {
                                displayName: subField.displayName,
                                id: fieldId,
                                value: subField.value,
                            };
                        }
                    });
                }
            });

        // Manually changing the values of trigger now, as it doesnt exist on payload
        const triggerJobField = this.getRecordField('triggerJob');
        const schedulerExpression = this.originalData?.schedulerExpression;
        if (triggerJobField) {
            if (!schedulerExpression || schedulerExpression === '') {
                triggerJobField.value = triggerJobField.defaultValue;
            } else {
                triggerJobField.value = true;
            }
        }
        this.checkForDefaultValuesInFields(this.recordData.fields);
        this.checkForDefaultValuesInSubsections(this.recordData.subSections);
    }

    getParentObject = (data, keys) => {
        let parentObject = data;
        keys.forEach(key => {
            parentObject = parentObject[key];
        });
        return parentObject;
    };

    getFileFieldIds = (fields: IntegrationConfigField[], subsections: IntegrationsSubSection[], tabs: IntegrationTabStructure[]) => {
        let fieldIds = [];
        fields?.forEach(field => {
            if (field.type === 'FILE') {
                fieldIds.push(field.id);
            }
        });
        subsections?.forEach(subSection => {
            fieldIds = [...fieldIds, ...this.getFileFieldIds(subSection?.fields, subSection?.childSubSections, undefined)];
            subSection?.childSubSections?.length > 0 &&
                (fieldIds = [...fieldIds, ...this.getFileFieldIds(undefined, subSection?.childSubSections, undefined)]);
            subSection?.tabs?.length > 0 && (fieldIds = [...fieldIds, ...this.getFileFieldIds(undefined, undefined, subSection?.tabs)]);
        });
        tabs?.forEach(tab => {
            fieldIds = [...fieldIds, ...this.getFileFieldIds(tab?.fields, undefined, undefined)];
        });
        return fieldIds;
    };

    checkForDefaultValuesInFields = (fields: IntegrationConfigField[], tab?: IntegrationTabStructure) => {
        fields
            ?.filter(field => field.value === undefined && field.defaultValueIf?.length > 0)
            .forEach(field => {
                const result = this.canAssignDefaultValue(field, tab, true);
                if (typeof result !== 'boolean') {
                    field.value = result;
                } else {
                    if (result) {
                        field.value = field.defaultValue;
                    } else {
                        field.value = typeof field.defaultValue === 'boolean' ? false : undefined;
                    }
                }
            });
    };

    checkForDefaultValuesInSubsections = (subsections: IntegrationsSubSection[]) => {
        subsections?.forEach(section => {
            if (section.haveTabs) {
                section.tabs.forEach(tab => {
                    if (tab.array) {
                        tab.records.forEach(record => {
                            this.checkForDefaultValuesInFields(record.fields, record as any);
                        });
                    } else {
                        this.checkForDefaultValuesInFields(tab.fields, tab);
                    }
                });
            } else if (section.array) {
                section.records.forEach(record => {
                    this.checkForDefaultValuesInFields(record.fields, record as any);
                    if (record.childSubSections?.length > 0) {
                        this.checkForDefaultValuesInSubsections(record.childSubSections);
                    }
                });
            } else {
                this.checkForDefaultValuesInFields(section.fields, section as any);
                if (section.childSubSections?.length > 0) {
                    this.checkForDefaultValuesInSubsections(section.childSubSections);
                }
            }
        });
    };

    // tslint:disable-next-line: whitespace
    addNewTabRecord? = (tab: IntegrationTabStructure) => {
        if (!tab.records) {
            tab.records = [];
        }
        if (tab.array) {
            const newRecord: IntegrationTabStructure['records'][0] = {
                fields: [],
                recordActions: [],
            };
            tab.fields.forEach(tabField => {
                const newTabField: IntegrationConfigField = CommonUtilsService.cloneObject(tabField);
                newTabField.value = newTabField.defaultValue;
                newRecord.fields.push(newTabField);
            });
            tab.recordActions?.forEach(tabField => {
                const newTabField: IntegrationConfigField = CommonUtilsService.cloneObject(tabField);
                newTabField.value = newTabField.defaultValue;
                newRecord.recordActions.push(newTabField);
            });
            tab.records.push(newRecord);
        }
    };

    addSubSectionRecord = (subSection: IntegrationsSubSection) => {
        const recordObject: IntegrationsSubSection['records'][0] = {
            fields: CommonUtilsService.cloneObject(subSection.fields),
        };
        if (subSection.hasChildSubSectons) {
            recordObject.childSubSections = [];
            recordObject.childSubSections = CommonUtilsService.cloneObject(subSection.childSubSections);
            // tslint:disable-next-line: no-use-before-declare
            this.assignSubSections(recordObject.childSubSections);
        }
        subSection.records.push(recordObject);
        this.checkForDefaultValuesInFields(this.recordData.fields);
        this.checkForDefaultValuesInSubsections(this.recordData.subSections);
    };

    private assignSubSections = (subSections: IntegrationsSubSection[]) => {
        subSections &&
            subSections.forEach(subSectionObject => {
                if (subSectionObject.haveTabs && subSectionObject.tabs.length > 0) {
                    subSectionObject.tabs.forEach(this.assignSubSectionTabRecord);
                } else if (subSectionObject.array) {
                    subSectionObject.records = [];
                    this.addSubSectionRecord(subSectionObject);
                }
            });
        this.checkForDefaultValuesInFields(this.recordData.fields);
        this.checkForDefaultValuesInSubsections(this.recordData.subSections);
    };

    private assignSubSectionTabRecord = (tab: IntegrationTabStructure) => {
        if (tab.array) {
            tab.records = tab.records || [];
            if (tab.records.length === 0) {
                tab.records.push({
                    fields: [],
                    recordActions: [],
                });
            }
            tab.records.forEach(tabRecord => {
                if (!tabRecord.fields || tabRecord.fields.length === 0) {
                    tab.fields.forEach(tabField => {
                        const newTabField: IntegrationConfigField = CommonUtilsService.cloneObject(tabField);
                        newTabField.value = newTabField.defaultValue;
                        tabRecord.fields.push(newTabField);
                    });
                } else if (tabRecord.fields.length !== tab.fields.length) {
                    tab.fields
                        .filter(action => tabRecord.fields.findIndex(recordAction => recordAction.id === action.id) === -1)
                        .forEach(action => {
                            const cloneField = CommonUtilsService.cloneObject(action);
                            cloneField.value = action.defaultValue;
                            tabRecord.fields.push(cloneField);
                        });
                }
                if (!tabRecord.recordActions || tabRecord.recordActions.length === 0) {
                    tab?.recordActions?.forEach(tabField => {
                        const newTabField: IntegrationConfigField = CommonUtilsService.cloneObject(tabField);
                        newTabField.value = newTabField.defaultValue;
                        tabRecord.recordActions.push(newTabField);
                    });
                } else if (tabRecord.recordActions.length !== tab.recordActions.length) {
                    tab.recordActions
                        .filter(action => tabRecord.recordActions.findIndex(recordAction => recordAction.id === action.id) === -1)
                        .forEach(action => {
                            const cloneAction = CommonUtilsService.cloneObject(action);
                            cloneAction.value = action.defaultValue;
                            tabRecord.recordActions.push(cloneAction);
                        });
                }
            });
        }
    };

    // tslint:disable-next-line: whitespace
    getRecordField? = (
        fieldId: string,
        tab?: IntegrationTabStructure,
        subSectionRecord?: IntegrationsSubSection['records'][0]
    ): IntegrationConfigField => {
        return this.getRecordFieldByProperty('id', fieldId, tab, subSectionRecord) as IntegrationConfigField;
    };

    // tslint:disable-next-line: whitespace
    getRecordTab? = (tabId: string): IntegrationTabStructure => {
        return this.getRecordTabByProperty('id', tabId) as IntegrationTabStructure;
    };

    // tslint:disable-next-line: whitespace
    setRecordFieldValue? = (fieldId: string, fieldProperty: string, value: any, tab?: IntegrationTabStructure) => {
        const field = this.getRecordField(fieldId, tab);
        field && (field[fieldProperty] = value);
    };

    // tslint:disable-next-line: whitespace
    getRecordFieldByProperty? = (
        propertyName: string,
        propertyValue: any,
        tab?: IntegrationTabStructure,
        subSectionRecord?: IntegrationsSubSection['records'][0]
    ): IntegrationConfigField | IntegrationConfigField[] => {
        let field: IntegrationConfigField | IntegrationConfigField[];
        if (subSectionRecord) {
            field = subSectionRecord.fields?.find(recordField => recordField[propertyName] === propertyValue);
        }
        if (!field && subSectionRecord?.childSubSections?.length > 0) {
            field = this.getRecordFieldFromSubSections(subSectionRecord.childSubSections, propertyName, propertyValue);
        }
        if (!field && tab) {
            field = this.getRecordFieldFromSubSections(Array.isArray(tab) ? tab : [tab as any], propertyName, propertyValue);
        }
        if (!field && this.recordData?.fields?.length > 0) {
            field = this.recordData?.fields?.find(recordField => recordField[propertyName] === propertyValue);
        }
        if (!field && this.recordData?.subSections?.length > 0) {
            field = this.getRecordFieldFromSubSections(this.recordData?.subSections, propertyName, propertyValue);
        }
        return field;
    };

    getRecordFieldFromSubSections = (subSections: IntegrationsSubSection[], propertyName: string, propertyValue: any) => {
        let field: IntegrationConfigField;
        const fields: IntegrationConfigField[] = [];
        subSections?.forEach(section => {
            if (field) {
                return;
            }
            if (propertyName === 'id' && section.id === propertyValue) {
                field = section as any;
            }
            if (section.array) {
                section.records?.forEach(sectionRecord => {
                    const fieldFromRecord = this.getRecordFieldFromSubSection(sectionRecord as any, propertyName, propertyValue) as any;
                    if (fieldFromRecord) {
                        fields.push(fieldFromRecord);
                    }
                });
            } else {
                field = this.getRecordFieldFromSubSection(section, propertyName, propertyValue) as any;
            }
        });
        return fields.length > 0 ? fields : field;
    };

    getRecordFieldFromSubSection = (section: IntegrationsSubSection, propertyName: string, propertyValue: any) => {
        let field: IntegrationConfigField;
        section.fields?.forEach(recordField => {
            if (field) {
                return;
            }
            if (recordField[propertyName] === propertyValue) {
                field = recordField;
            }
            if (!field && (recordField as any).childSubSections?.length > 0) {
                field = this.getRecordFieldFromSubSections((recordField as any).childSubSections, propertyName, propertyValue) as any;
            }
            if (!field && (recordField as any).tabs?.length > 0) {
                field = this.getRecordFieldFromSubSections((recordField as any).tabs, propertyName, propertyValue) as any;
            }
        });
        if (!field && (section as any).childSubSections?.length > 0) {
            field = this.getRecordFieldFromSubSections((section as any).childSubSections, propertyName, propertyValue) as any;
        }
        if (!field && (section as any).tabs?.length > 0) {
            field = this.getRecordFieldFromSubSections((section as any).tabs, propertyName, propertyValue) as any;
        }
        return field;
    };

    getRecordTabByProperty? = (propertyName: string, propertyValue: any): IntegrationTabStructure => {
        let tab: IntegrationTabStructure;
        this.recordData?.subSections?.forEach(section => {
            if (section?.haveTabs) {
                section?.tabs?.forEach(sectionTab => {
                    if (sectionTab[propertyName] === propertyValue) {
                        tab = sectionTab;
                    }
                });
            }
            if (!tab && section.childSubSections) {
                section.childSubSections.forEach(subSection => {
                    subSection?.tabs?.forEach(sectionTab => {
                        if (sectionTab[propertyName] === propertyValue) {
                            tab = sectionTab;
                        }
                    });
                });
            }
        });
        if (tab) {
            return tab;
        }
    };

    // tslint:disable-next-line: whitespace
    getRecordFieldValueByProperty? = (propertyName: string, propertyValue: any): string | boolean | string[] => {
        const field = this.getRecordFieldByProperty(propertyName, propertyValue);
        if (!Array.isArray(field)) {
            return field && field.value;
        }
    };

    // tslint:disable-next-line: whitespace
    getMasterField? = (): IntegrationConfigField => {
        const field = this.recordData.fields.find(recordField => recordField.isMasterField);
        if (field) {
            return field;
        }
    };

    // tslint:disable-next-line: whitespace
    canHideField? = (
        field: IntegrationConfigField,
        tab?: IntegrationTabStructure,
        subSectionRecord?: IntegrationsSubSection['records'][0]
    ) => {
        if (field?.show === false) {
            return true;
        }
        const masterField = this.getMasterField();
        const masterValue = masterField && masterField.value;
        let satisfied = false;
        if (field.conditionalDisplay) {
            let satisfiedConditions;
            field.conditionalDisplay &&
                field.conditionalDisplay.forEach(conditionalDisplay => {
                    switch (conditionalDisplay.type) {
                        case 'ALL_MUST_SATISFY':
                            satisfiedConditions = conditionalDisplay.conditions.filter(condition => {
                                const requiredField = this.getRecordField(condition.fieldId, tab, subSectionRecord);
                                let requiredValue = requiredField && requiredField.value;
                                if (requiredField === undefined) {
                                    requiredValue = this[condition.fieldId];
                                }
                                return condition.valueExists
                                    ? requiredValue !== undefined
                                    : condition.requiredValue !== undefined
                                    ? condition.requiredValue === requiredValue
                                    : false;
                            });
                            satisfied = satisfied || satisfiedConditions.length === conditionalDisplay.conditions.length;
                            break;
                        case 'ANY_SATISFY':
                            satisfiedConditions = conditionalDisplay.conditions.filter(condition => {
                                const requiredField = this.getRecordField(condition.fieldId, tab, subSectionRecord);
                                let requiredValue = requiredField && requiredField.value;
                                if (requiredField === undefined) {
                                    requiredValue = this[condition.fieldId];
                                }
                                return condition.valueExists
                                    ? requiredValue !== undefined
                                    : condition.requiredValue !== undefined
                                    ? condition.requiredValue === requiredValue
                                    : false;
                            });
                            satisfied = satisfied || satisfiedConditions.length > 0;
                            break;
                        default:
                            break;
                    }
                });
            if (!satisfied) {
                if (!tab && masterField) {
                    if (masterValue === false) {
                        field.value = field.defaultValue;
                    } else if (masterValue === true) {
                        field.value = undefined;
                    }
                } else if (!tab && !masterField) {
                    field.value = field.defaultValue;
                } else if (tab) {
                    if (tab.array) {
                        /** @todo Do nothing for now */
                    } else {
                        field.value = field.value || field.defaultValue || undefined;
                    }
                }
            }
            field['hide'] = !satisfied;
            return !satisfied;
        }
        return satisfied;
    };

    // tslint:disable-next-line: whitespace
    canAssignDefaultValue? = (field: IntegrationConfigField, tab?: IntegrationTabStructure['records'][0], returnDefaultValue?: boolean) => {
        let satisfied = false;
        let defaultValue: any;
        if (field.defaultValueIf) {
            let satisfiedConditions;
            field.defaultValueIf &&
                field.defaultValueIf.forEach(conditionalDefault => {
                    switch (conditionalDefault.type) {
                        case 'ALL_MUST_SATISFY':
                            satisfiedConditions = conditionalDefault.conditions.filter(condition => {
                                const requiredField = this.getRecordField(condition.fieldId, tab as any);
                                let requiredValue = requiredField && requiredField.value;
                                if (requiredField === undefined) {
                                    requiredValue = this[condition.fieldId];
                                }
                                if (requiredField as any as IntegrationsSubSection) {
                                    if (requiredField?.array) {
                                        const definedFields =
                                            (requiredField as any as IntegrationsSubSection)?.records?.filter(
                                                record => record?.fields?.filter(recordField => recordField.value !== undefined)?.length > 0
                                            )?.length > 0;
                                        requiredValue =
                                            (requiredField as any as IntegrationsSubSection)?.records?.length > 0 && definedFields
                                                ? true
                                                : undefined;
                                    } else {
                                        const definedFields =
                                            (requiredField as any as IntegrationsSubSection)?.fields?.filter(
                                                recordField => recordField.value !== undefined
                                            )?.length > 0;
                                        requiredValue = definedFields ? true : undefined;
                                    }
                                }
                                return condition.valueExists
                                    ? requiredValue !== undefined
                                    : condition.requiredValue !== undefined
                                    ? condition.requiredValue === requiredValue
                                    : false;
                            });
                            satisfied = satisfied || satisfiedConditions.length === conditionalDefault.conditions.length;
                            break;
                        case 'ANY_SATISFY':
                            satisfiedConditions = conditionalDefault.conditions.filter(condition => {
                                const requiredField = this.getRecordField(condition.fieldId);
                                let requiredValue = requiredField && requiredField.value;
                                if (requiredField === undefined) {
                                    requiredValue = this[condition.fieldId];
                                }
                                return condition.valueExists
                                    ? requiredValue !== undefined
                                    : condition.requiredValue !== undefined
                                    ? condition.requiredValue === requiredValue
                                    : false;
                            });
                            satisfied = satisfied || satisfiedConditions.length > 0;
                            break;
                        default:
                            break;
                    }
                    if (satisfied && conditionalDefault.defaultValue !== undefined) {
                        defaultValue = conditionalDefault.defaultValue;
                    }
                });
        }
        return satisfied ? (defaultValue !== undefined ? (returnDefaultValue ? defaultValue : true) : true) : false;
    };

    // tslint:disable-next-line: whitespace
    canDisableField? = (
        field: IntegrationConfigField,
        tab?: IntegrationTabStructure,
        subSectionRecord?: IntegrationsSubSection['records'][0]
    ) => {
        let satisfied = false;
        if (field.disabled) {
            satisfied = true;
        }
        if (!satisfied && field.conditionalDisable) {
            let satisfiedConditions;
            field.conditionalDisable?.forEach(conditionalDisable => {
                switch (conditionalDisable.type) {
                    case 'ALL_MUST_SATISFY':
                        satisfiedConditions = conditionalDisable.conditions.filter(condition => {
                            const requiredField = this.getRecordField(condition.fieldId, tab, subSectionRecord);
                            let requiredValue = requiredField && requiredField.value;
                            if (requiredField === undefined) {
                                requiredValue = this[condition.fieldId];
                            }
                            if (condition.valueExists !== undefined) {
                                if (condition.valueExists) {
                                    return requiredValue !== undefined;
                                } else {
                                    return requiredValue === undefined || requiredValue === '';
                                }
                            }
                            return condition.requiredValue !== undefined ? condition.requiredValue === requiredValue : false;
                        });
                        satisfied = satisfied || satisfiedConditions.length === conditionalDisable.conditions.length;
                        break;
                    case 'ANY_SATISFY':
                        satisfiedConditions = conditionalDisable.conditions.filter(condition => {
                            const requiredField = this.getRecordField(condition.fieldId, tab, subSectionRecord);
                            let requiredValue = requiredField && requiredField.value;
                            if (requiredField === undefined) {
                                requiredValue = this[condition.fieldId];
                            }
                            if (condition.valueExists !== undefined) {
                                if (condition.valueExists) {
                                    return requiredValue !== undefined;
                                } else {
                                    return requiredValue === undefined || requiredValue === '';
                                }
                            }
                            return condition.requiredValue === requiredValue;
                        });
                        satisfied = satisfied || satisfiedConditions.length > 0;
                        break;
                    default:
                        break;
                }
            });
        }
        field.disabledViaCondition = satisfied;
        return satisfied;
    };

    canHideSubSection = (subSection: IntegrationsSubSection, subSectionRecord?: IntegrationsSubSection['records'][0]) => {
        let satisfied = false;
        if (subSection.conditionalDisplay) {
            let satisfiedConditions;
            subSection.conditionalDisplay?.forEach(conditionalDisplay => {
                switch (conditionalDisplay.type) {
                    case 'ALL_MUST_SATISFY':
                        satisfiedConditions = conditionalDisplay.conditions.filter(condition => {
                            const requiredField = this.getRecordField(condition.fieldId, undefined, subSectionRecord);
                            return condition.valueExists
                                ? requiredField?.value !== undefined
                                : condition.requiredValue !== undefined
                                ? condition.requiredValue === requiredField?.value
                                : false;
                        });
                        satisfied = satisfied || satisfiedConditions.length === conditionalDisplay.conditions.length;
                        break;

                    case 'ANY_SATISFY':
                        satisfiedConditions = conditionalDisplay.conditions.filter(condition => {
                            const requiredField = this.getRecordField(condition.fieldId, undefined, subSectionRecord);
                            if (Array.isArray(requiredField)) {
                                return requiredField.some(field => {
                                    let requiredValue = field?.value;

                                    if (field === undefined) {
                                        requiredValue = this[condition.fieldId];
                                    }
                                    return condition.valueExists
                                        ? requiredValue !== undefined
                                        : condition.requiredValue !== undefined
                                        ? condition.requiredValue === requiredValue
                                        : false;
                                });
                            } else {
                                let requiredValue = requiredField?.value;
                                if (requiredField === undefined) {
                                    requiredValue = this[condition.fieldId];
                                }
                                return condition.valueExists
                                    ? requiredValue !== undefined
                                    : condition.requiredValue !== undefined
                                    ? condition.requiredValue === requiredValue
                                    : false;
                            }
                        });
                        satisfied = satisfied || satisfiedConditions.length > 0;
                        break;
                    default:
                        break;
                }
            });
            if (!satisfied) {
                this.applyDefaultValueToSubsection(subSection);
            }
            return !satisfied;
        }
        return satisfied;
    };

    applyDefaultValueToSubsection = (subSection: IntegrationsSubSection) => {
        if (subSection?.array) {
            subSection?.records?.forEach(subSectionRecord => this.applyDefaultValueToRecord(subSectionRecord));
        } else {
            if (subSection?.fields) {
                subSection.fields?.forEach(subSectionField => {
                    subSectionField.value = subSectionField.defaultValue !== undefined ? subSectionField.defaultValue : undefined;
                });
            }
            if (subSection?.childSubSections?.length > 0) {
                subSection?.childSubSections?.forEach(childSubSection => this.applyDefaultValueToSubsection(childSubSection));
            }
            if (subSection?.tabs?.length > 0) {
                subSection.tabs.forEach(tab => this.applyDefaultValueToSubsection(tab as any));
            }
        }
    };

    applyDefaultValueToRecord = (record: IntegrationSubSectionRecord) => {
        record?.fields?.forEach(recordField => {
            recordField.value = recordField.defaultValue !== undefined ? recordField.defaultValue : undefined;
        });
        if (record.childSubSections?.length > 0) {
            record.childSubSections.forEach(childSubSection => this.applyDefaultValueToRecord(childSubSection));
        }
        if ((record as any as IntegrationsSubSection)?.tabs?.length > 0) {
            (record as any as IntegrationsSubSection).tabs.forEach(tab => this.applyDefaultValueToSubsection(tab as any));
        }
    };

    isConditionalArray = (field: IntegrationConfigField) => {
        let satisfied = false;
        let satisfiedConditions: { fieldId: string; requiredValue: boolean; array: boolean }[];
        field.conditionalArray &&
            field.conditionalArray.forEach(conditions => {
                if (satisfied) {
                    return;
                }
                switch (conditions.type) {
                    case 'ALL_MUST_SATISFY':
                        satisfiedConditions = conditions.conditions.filter(condition => {
                            const requiredField = this.getRecordField(condition.fieldId, undefined);
                            let requiredValue = requiredField && requiredField.value;
                            if (requiredField === undefined) {
                                requiredValue = this[condition.fieldId];
                            }
                            return condition.requiredValue !== undefined ? condition.requiredValue === requiredValue : false;
                        });
                        satisfied = satisfied || satisfiedConditions.length === conditions.conditions.length;
                        break;
                    case 'ANY_SATISFY':
                        satisfiedConditions = conditions.conditions.filter(condition => {
                            const requiredField = this.getRecordField(condition.fieldId);
                            let requiredValue = requiredField && requiredField.value;
                            if (requiredField === undefined) {
                                requiredValue = this[condition.fieldId];
                            }
                            return condition.requiredValue !== undefined ? condition.requiredValue === requiredValue : false;
                        });
                        satisfied = satisfied || satisfiedConditions.length > 0;
                        break;
                    default:
                        break;
                }
            });
        return satisfied && satisfiedConditions && satisfiedConditions[0] && satisfiedConditions[0].array;
    };

    getRecordFieldParentObjectByProperty? = (propertyName: string, propertyValue: any, tab?: IntegrationTabStructure): any => {
        if (tab) {
            const parentObject = this.getRecordFieldParentObjectFromTab(tab, propertyName, propertyValue);
            if (parentObject) {
                return parentObject;
            }
        } else {
            let parentObject = this.getRecordFieldParentObjectFromFields(
                this.recordData,
                this.recordData.fields,
                propertyName,
                propertyValue
            );
            if (!parentObject) {
                parentObject = this.getRecordFieldParentObjectFromSubSections(this.recordData.subSections, propertyName, propertyValue);
            }
            if (parentObject) {
                return parentObject;
            }
        }
    };

    getRecordFieldParentObjectFromFields = (
        parent: any,
        fields: IntegrationConfigField[],
        propertyName: string,
        propertyValue: any
    ): any => {
        return fields.find(field => field[propertyName] === propertyValue) ? parent : undefined;
    };

    getRecordFieldParentObjectFromSubSections = (subsections: IntegrationsSubSection[], propertyName: string, propertyValue: any): any => {
        let parentObject;
        subsections?.forEach(section => {
            !parentObject &&
                (parentObject = this.getRecordFieldParentObjectFromFields(section, section.fields, propertyName, propertyValue));
            if (!parentObject && section.id === propertyValue) {
                parentObject = section as any;
                return;
            }
            if (!parentObject && section.childSubSections?.length > 0) {
                parentObject = this.getRecordFieldParentObjectFromSubSections(section.childSubSections, propertyName, propertyValue);
            }
            if (!parentObject && section.tabs?.length > 0) {
                parentObject = this.getRecordFieldParentObjectFromTabs(section.tabs, propertyName, propertyValue);
            }
        });
        return parentObject;
    };

    getRecordFieldParentObjectFromTabs = (
        tabs: IntegrationTabStructure[],
        propertyName: string,
        propertyValue: any
    ): IntegrationConfigField => {
        let parentObject;
        tabs?.forEach(tab => {
            if (parentObject) {
                return;
            }
            parentObject = this.getRecordFieldParentObjectFromTab(tab, propertyName, propertyValue);
        });
        return parentObject;
    };

    getRecordFieldParentObjectFromTab = (
        tab: IntegrationTabStructure,
        propertyName: string,
        propertyValue: any
    ): IntegrationConfigField => {
        let parentObject = this.getRecordFieldParentObjectFromFields(tab, tab.fields, propertyName, propertyValue);
        if (!parentObject) {
            parentObject = tab.recordActions?.find(recordField => recordField[propertyName] === propertyValue) ? tab : undefined;
        }
        if (!parentObject && tab.childSubSections) {
            parentObject = this.getRecordFieldParentObjectFromSubSections(tab.childSubSections, propertyName, propertyValue);
        }
        return parentObject;
    };
}
